import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BRAND = { dark: [26, 32, 21], olive: [136, 136, 100], accent: [69, 83, 50] };

function addFooter(pdf) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  const pages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(...BRAND.olive);
    pdf.text('CONFIDENTIAL — SusTek Manufacturing — CBAM Supplier Communication Pack', 10, h - 5);
    pdf.text(`Page ${i} of ${pages}`, w - 10, h - 5, { align: 'right' });
  }
}

export async function exportSupplierCommPack(installation, complianceData) {
  const overlay = document.createElement('div');
  overlay.id = 'pdf-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(26,32,21,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
  overlay.innerHTML = '<div style="background:white;padding:24px 40px;border-radius:12px;text-align:center;font-family:Manrope,sans-serif"><p style="font-size:14px;font-weight:600;color:#1A2015">Generating Supplier Communication Pack...</p><p style="font-size:12px;color:#888864;margin-top:4px">' + installation.site_name + '</p></div>';
  document.body.appendChild(overlay);

  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const w = pdf.internal.pageSize.getWidth();
    const inst = installation;
    const r = inst.readiness;

    // ───── COVER PAGE ─────
    pdf.setFillColor(...BRAND.dark);
    pdf.rect(0, 0, w, 297, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(24);
    pdf.setTextColor(255, 255, 255);
    pdf.text('CBAM Supplier', 20, 50);
    pdf.text('Communication Pack', 20, 62);

    pdf.setFontSize(13);
    pdf.setTextColor(...BRAND.olive);
    pdf.text(`${inst.site_name} — ${inst.operator_entity}`, 20, 78);

    pdf.setDrawColor(136, 136, 100);
    pdf.setLineWidth(0.5);
    pdf.line(20, 85, 120, 85);

    let cy = 100;
    const coverData = [
      ['Installation ID', inst.installation_id],
      ['Operator Entity', inst.operator_entity],
      ['Country', `${inst.country} (${inst.country_code})`],
      ['Verification Status', inst.verification_status],
      ['Products', inst.products.map(p => p.description).join(', ')],
      ['Readiness Score', `${r.pct}%`],
      ['Items Complete', `${r.complete} of ${r.total}`],
      ['Items Partial', String(r.partial)],
      ['Items Missing', String(r.missing)],
      ['Est. Generation Time', inst.pack_generation.estimated_time],
      ['Generated', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
    ];
    for (const [label, value] of coverData) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(...BRAND.olive);
      pdf.text(label, 20, cy);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text(String(value).substring(0, 80), 80, cy);
      cy += 8;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 80);
    pdf.text('This pack contains the data your EU customer (Authorised CBAM Declarant) requires', 20, 220);
    pdf.text('under EU Regulation 2023/956 and Implementing Regulation 2023/1773.', 20, 225);
    pdf.text('Share this document with your EU buyer to support their CBAM quarterly declaration.', 20, 230);

    // ───── PAGE 2: WHAT YOUR EU BUYER NEEDS (summary) ─────
    pdf.addPage();
    pdf.setFillColor(...BRAND.dark);
    pdf.rect(0, 0, w, 18, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Section 1: What Your EU Buyer Needs from You', 10, 12);

    let y = 26;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...BRAND.dark);
    pdf.text('Under the EU CBAM regulation, your EU customer (the Authorised Declarant) must submit quarterly declarations', 10, y);
    y += 5;
    pdf.text('detailing the embedded emissions in imported goods. As the supplier, you must provide the following data:', 10, y);
    y += 8;

    const categories = [...new Set(inst.checklist.map(c => c.category))];
    for (const cat of categories) {
      const items = inst.checklist.filter(c => c.category === cat);
      pdf.setFillColor(...BRAND.accent);
      pdf.rect(10, y, w - 20, 6, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(255, 255, 255);
      pdf.text(cat, 14, y + 4);
      y += 9;

      for (const item of items) {
        if (y > 270) { pdf.addPage(); y = 15; }
        const statusLabel = item.status === 'complete' ? '[COMPLETE]' : item.status === 'partial' ? '[PARTIAL]' : item.status === 'not_applicable' ? '[N/A]' : '[MISSING]';
        const statusColor = item.status === 'complete' ? [22, 163, 74] : item.status === 'partial' ? [217, 119, 6] : item.status === 'not_applicable' ? [100, 116, 139] : [220, 38, 38];

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(...statusColor);
        pdf.text(statusLabel, 14, y);

        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...BRAND.dark);
        pdf.text(item.requirement, 36, y, { maxWidth: w - 56 });
        y += 4;

        pdf.setFontSize(6.5);
        pdf.setTextColor(...BRAND.olive);
        pdf.text(`Ref: ${item.regulation_ref}`, 36, y);
        y += 3.5;
        pdf.text(`Status: ${item.notes}`, 36, y, { maxWidth: w - 56 });
        y += 5;
      }
      y += 2;
    }

    // ───── PAGE 3: COMPLIANCE STATUS TABLE ─────
    pdf.addPage();
    pdf.setFillColor(...BRAND.dark);
    pdf.rect(0, 0, w, 18, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Section 2: Compliance Checklist Summary', 10, 12);

    autoTable(pdf, {
      startY: 24,
      margin: { left: 10, right: 10 },
      head: [['ID', 'Category', 'Requirement', 'Status', 'Regulation Ref']],
      body: inst.checklist.map(item => [
        item.id,
        item.category,
        item.requirement.substring(0, 60) + (item.requirement.length > 60 ? '...' : ''),
        item.status === 'complete' ? 'COMPLETE' : item.status === 'partial' ? 'PARTIAL' : item.status === 'not_applicable' ? 'N/A' : 'MISSING',
        item.regulation_ref,
      ]),
      styles: { fontSize: 6.5, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
      didParseCell: (data) => {
        if (data.column.index === 3 && data.section === 'body') {
          const val = data.cell.raw;
          if (val === 'COMPLETE') data.cell.styles.textColor = [22, 163, 74];
          else if (val === 'PARTIAL') data.cell.styles.textColor = [217, 119, 6];
          else if (val === 'MISSING') data.cell.styles.textColor = [220, 38, 38];
          else data.cell.styles.textColor = [100, 116, 139];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    // ───── PAGE 4: EU BUYERS REQUESTING DATA ─────
    const buyers = (complianceData.buyer_requests || []).filter(b =>
      b.products_requested.some(p =>
        inst.products.some(ip => ip.description === p)
      )
    );

    if (buyers.length > 0) {
      pdf.addPage();
      pdf.setFillColor(...BRAND.dark);
      pdf.rect(0, 0, w, 18, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Section 3: EU Customers Requesting This Data', 10, 12);

      autoTable(pdf, {
        startY: 24,
        margin: { left: 10, right: 10 },
        head: [['EU Importer', 'Country', 'Declarant ID', 'Products', 'Shipments', 'Volume (t)', 'CBAM Exposure (EUR)', 'Urgency']],
        body: buyers.map(b => [
          b.importer,
          b.country,
          b.declarant_id,
          b.products_requested.map(p => p.length > 18 ? p.slice(0, 18) + '...' : p).join(', '),
          String(b.shipment_count),
          String(b.total_tonnes),
          `EUR ${b.total_net_eur?.toLocaleString()}`,
          b.urgency.toUpperCase(),
        ]),
        styles: { fontSize: 6.5, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
        headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
        alternateRowStyles: { fillColor: [245, 245, 240] },
        didParseCell: (data) => {
          if (data.column.index === 7 && data.section === 'body') {
            const val = data.cell.raw;
            if (val === 'HIGH') data.cell.styles.textColor = [220, 38, 38];
            else if (val === 'MEDIUM') data.cell.styles.textColor = [217, 119, 6];
            else data.cell.styles.textColor = [22, 163, 74];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
    }

    // ───── GAP ANALYSIS PAGE ─────
    const gaps = inst.checklist.filter(c => c.status === 'missing' || c.status === 'partial');
    if (gaps.length > 0) {
      pdf.addPage();
      pdf.setFillColor(...BRAND.dark);
      pdf.rect(0, 0, w, 18, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Section 4: Gap Analysis & Remediation Actions', 10, 12);

      autoTable(pdf, {
        startY: 24,
        margin: { left: 10, right: 10 },
        head: [['Priority', 'ID', 'Requirement', 'Current Status', 'Gap Detail', 'Regulation']],
        body: gaps.map((g, i) => [
          g.status === 'missing' ? 'CRITICAL' : 'RECOMMENDED',
          g.id,
          g.requirement.substring(0, 55) + (g.requirement.length > 55 ? '...' : ''),
          g.status.toUpperCase(),
          g.notes,
          g.regulation_ref,
        ]),
        styles: { fontSize: 6.5, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
        headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
        alternateRowStyles: { fillColor: [245, 245, 240] },
        didParseCell: (data) => {
          if (data.column.index === 0 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = data.cell.raw === 'CRITICAL' ? [220, 38, 38] : [217, 119, 6];
          }
        },
      });
    }

    addFooter(pdf);
    pdf.save(`SusTek_CBAM_Supplier_Pack_${inst.site_name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('Supplier pack export failed:', err);
    alert(`Export failed: ${err.message}`);
  } finally {
    const o = document.getElementById('pdf-overlay');
    if (o) o.remove();
  }
}
